import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import type { CreateTokenInput, PersonalAccessToken, TokenKind } from "@tunic/shared";
import { api } from "@/api/client";
import { qk } from "@/api/keys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, SelectField } from "@/components/projects/ProjectForm";
import { RelativeTime } from "@/components/common/RelativeTime";
import { EmptyState } from "@/components/common/EmptyState";

export const Route = createFileRoute("/_app/settings/tokens")({ component: TokensPage });

const KIND_LABEL: Record<TokenKind, string> = { mcp: "Claude Code / MCP", cli: "CLI or scripts", mobile: "Mobile app" };

function TokensPage() {
  const qc = useQueryClient();
  const tokens = useQuery({ queryKey: qk.tokens, queryFn: () => api.get<PersonalAccessToken[]>("/api/tokens") });
  const create = useMutation({
    mutationFn: (input: CreateTokenInput) => api.post<{ token: string; pat: PersonalAccessToken }>("/api/tokens", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tokens }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/tokens/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.tokens });
      toast.success("Token revoked");
    },
  });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<TokenKind>("mcp");
  const [secret, setSecret] = useState<string | null>(null);

  const submit = async () => {
    const r = await create.mutateAsync({ name, kind });
    setSecret(r.token);
    setName("");
  };

  const origin = window.location.origin;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Personal access tokens</h2>
          <p className="text-sm text-muted-foreground">For Claude Code (MCP), scripts, and the mobile app.</p>
        </div>
        <Button onClick={() => setOpen(true)}>New token</Button>
      </div>
      {tokens.data?.length === 0 ? (
        <EmptyState title="No tokens yet" />
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {tokens.data?.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground">
                  <code>{t.token_prefix}...</code> · {KIND_LABEL[t.kind]} · created <RelativeTime iso={t.created_at} />
                  {t.last_used_at && (
                    <>
                      {" "}
                      · last used <RelativeTime iso={t.last_used_at} />
                    </>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-danger" onClick={() => revoke.mutate(t.id)}>
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setSecret(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{secret ? "Copy your token" : "New access token"}</DialogTitle>
          </DialogHeader>
          {secret ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">This is the only time the token is shown. Store it somewhere safe.</p>
              <div className="flex gap-2">
                <Input readOnly value={secret} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    toast.success("Copied");
                  }}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
              {kind === "mcp" && (
                <div className="rounded-md bg-muted p-3 text-xs">
                  <div className="mb-1 font-medium">Add to Claude Code:</div>
                  <code className="break-all">
                    claude mcp add --transport http tunic {origin}/mcp --header "Authorization: Bearer {secret}"
                  </code>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setOpen(false)}>I have saved it</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Name">
                <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Laptop Claude Code" />
              </Field>
              <Field label="Used for">
                <SelectField value={kind} onChange={setKind} options={KIND_LABEL} />
              </Field>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!name.trim() || create.isPending}>
                  Create
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
